import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "./ui/Input";
import { Button } from "./ui/Button";
import { Stack } from "./ui/Stack";
import { useStore } from "../context/StoreContext";
import { useCurrency } from "../context/CurrencyContext";
import { useApi } from "../context/ApiContext";
import { COUNTRIES, getCountryByCode } from "../utils/countryCurrencyData";

export interface Address {
  _id?: string;
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  postalCode: string;
  country: string;
  phone?: string;
  type?: "shipping" | "billing" | "both";
  isDefault?: boolean;
}

interface AddressFormProps {
  address: Address;
  onChange: (address: Address) => void;
  onSubmit?: (e: React.FormEvent) => void;
  onCancel?: () => void;
  submitLabel?: string;
  showType?: boolean;
  showDefault?: boolean;
  errors?: Record<string, string>;
  formId?: string;
}

export const AddressForm: React.FC<AddressFormProps> = ({
  address,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Save Address",
  showType = false,
  showDefault = false,
  errors = {},
  formId,
}) => {
  const { t } = useTranslation();
  const { store } = useStore();
  const { location: currentLocation } = useCurrency();
  const { api } = useApi();
  
  const [availableCountries, setAvailableCountries] = useState<Array<{ code: string; name: string }>>([]);

  // Fetch store locations to determine available countries
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get("/locations/admin?isActive=true");
        const locations = response.data.locations || [];

        // Build list of available countries based on locations
        const allMappedCountries = new Set<string>();
        let defaultLocation: typeof locations[0] | null = null;

        // Collect all mapped countries and find default location
        locations.forEach((loc: any) => {
          if (loc.default) {
            defaultLocation = loc;
          }
          if (loc.mappedCountryCodes && loc.mappedCountryCodes.length > 0) {
            loc.mappedCountryCodes.forEach((code: string) => {
              allMappedCountries.add(code.toUpperCase());
            });
          }
        });

        // Build available countries list
        const available: Array<{ code: string; name: string }> = [];

        locations.forEach((loc: any) => {
          const countryCode = loc.countryCode.toUpperCase();
          const country = getCountryByCode(countryCode);
          
          // Add the location's own country
          if (country) {
            available.push({ code: countryCode, name: country.name });
          }

          // Add mapped countries for this location
          if (loc.mappedCountryCodes && loc.mappedCountryCodes.length > 0) {
            loc.mappedCountryCodes.forEach((mappedCode: string) => {
              const mappedCountry = getCountryByCode(mappedCode);
              if (mappedCountry && !available.some(c => c.code === mappedCode.toUpperCase())) {
                available.push({ code: mappedCode.toUpperCase(), name: mappedCountry.name });
              }
            });
          }
        });

        // If there's a default location, add all non-mapped countries
        if (defaultLocation) {
          COUNTRIES.forEach((country) => {
            const countryCodeUpper = country.code.toUpperCase();
            if (!allMappedCountries.has(countryCodeUpper) && 
                !available.some(c => c.code === countryCodeUpper)) {
              available.push({ code: countryCodeUpper, name: country.name });
            }
          });
        }

        // Sort by name
        available.sort((a, b) => a.name.localeCompare(b.name));
        setAvailableCountries(available);
      } catch (err) {
        // On error, fallback to all countries
        const allCountries = COUNTRIES.map(c => ({ code: c.code, name: c.name }));
        allCountries.sort((a, b) => a.name.localeCompare(b.name));
        setAvailableCountries(allCountries);
      }
    };

    fetchLocations();
  }, [api]);

  // Preset country to current location if address doesn't have one
  useEffect(() => {
    if (!address.country && currentLocation?.countryCode) {
      onChange({ ...address, country: currentLocation.countryCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation?.countryCode]);

  // Get country from address (normalize to uppercase 2-letter code)
  const addressCountry = address.country || currentLocation?.countryCode || "";
  const normalizedCountry = addressCountry ? addressCountry.toUpperCase().substring(0, 2) : "";

  // Get mandatory fields - check country-specific first, then fall back to global
  const mandatoryAddressFieldsByCountry = store?.settings?.mandatoryAddressFieldsByCountry || {};
  const mandatoryFields = normalizedCountry && mandatoryAddressFieldsByCountry[normalizedCountry]
    ? mandatoryAddressFieldsByCountry[normalizedCountry]
    : store?.settings?.mandatoryAddressFields || [
        "firstName",
        "lastName",
        "address1",
        "city",
        "postalCode",
        "country",
      ];

  const isFieldRequired = (fieldName: string): boolean => {
    return mandatoryFields.includes(fieldName);
  };

  const getFieldLabel = (fieldName: string, defaultLabel: string): string => {
    const isRequired = isFieldRequired(fieldName);
    return isRequired ? defaultLabel : `${defaultLabel} (Optional)`;
  };

  const handleChange = (field: keyof Address, value: string | boolean) => {
    const updatedAddress = { ...address, [field]: value };
    onChange(updatedAddress);
  };

  return (
    <form id={formId} onSubmit={onSubmit}>
      <Stack spacing="md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={getFieldLabel("firstName", "First Name")}
            required={isFieldRequired("firstName")}
            value={address.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            error={errors.firstName}
          />
          <Input
            label={getFieldLabel("lastName", "Last Name")}
            required={isFieldRequired("lastName")}
            value={address.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            error={errors.lastName}
          />
          {showType && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address Type
              </label>
              <select
                value={address.type || "shipping"}
                onChange={(e) => handleChange("type", e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="shipping">Shipping Address</option>
                <option value="billing">Billing Address</option>
                <option value="both">Both</option>
              </select>
            </div>
          )}
          <Input
            label={getFieldLabel("company", "Company")}
            required={isFieldRequired("company")}
            value={address.company || ""}
            onChange={(e) => handleChange("company", e.target.value)}
            className="md:col-span-2"
            error={errors.company}
          />
          <Input
            label={getFieldLabel("address1", "Address Line 1")}
            required={isFieldRequired("address1")}
            value={address.address1}
            onChange={(e) => handleChange("address1", e.target.value)}
            className="md:col-span-2"
            error={errors.address1}
          />
          <Input
            label={getFieldLabel("address2", "Address Line 2")}
            required={isFieldRequired("address2")}
            value={address.address2 || ""}
            onChange={(e) => handleChange("address2", e.target.value)}
            className="md:col-span-2"
            error={errors.address2}
          />
          <Input
            label={getFieldLabel("city", "City")}
            required={isFieldRequired("city")}
            value={address.city}
            onChange={(e) => handleChange("city", e.target.value)}
            error={errors.city}
          />
          <Input
            label={getFieldLabel("province", "Province/State")}
            required={isFieldRequired("province")}
            value={address.province || ""}
            onChange={(e) => handleChange("province", e.target.value)}
            error={errors.province}
          />
          <Input
            label={getFieldLabel("postalCode", "Postal Code")}
            required={isFieldRequired("postalCode")}
            value={address.postalCode}
            onChange={(e) => handleChange("postalCode", e.target.value)}
            error={errors.postalCode}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {getFieldLabel("country", "Country")}
              {isFieldRequired("country") && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={address.country || currentLocation?.countryCode || ""}
              onChange={(e) => handleChange("country", e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.country ? "border-red-500" : "border-gray-300"
              }`}
              required={isFieldRequired("country")}
            >
              <option value="">{t("checkout.selectCountry") || "Select a country"}</option>
              {availableCountries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="mt-1 text-sm text-red-600">{errors.country}</p>
            )}
          </div>
          <Input
            label={getFieldLabel("phone", "Phone")}
            type="tel"
            required={isFieldRequired("phone")}
            value={address.phone || ""}
            onChange={(e) => handleChange("phone", e.target.value)}
            className="md:col-span-2"
            error={errors.phone}
          />
          {showDefault && (
            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={address.isDefault || false}
                  onChange={(e) => handleChange("isDefault", e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  Set as default address
                </span>
              </label>
            </div>
          )}
        </div>
        {onCancel !== undefined && (
          <div className="flex gap-3 justify-end">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit">{submitLabel}</Button>
          </div>
        )}
      </Stack>
    </form>
  );
};
